import { MidGrid } from "../../styles/emit-styles/Emit-styles";
import CardGrid from "./CardGrid";
import DistributionCard from "./DistributionCard";
import GraphCard from "./GraphCard";
import { compileDistanceBreakdownByDrivetrainData, compileFleetCompositionCardData, compileGeneratedEmissionsByDrivetrainData, compileGeneratedEmissionsViewGraphData, formatData, getEmissionsUnits } from "./UtilityFunctions";
import { combinedEmissionsBars, emissionsByDrivetrainBars, fleetDrivetrains, GENERATED_TABLE_TYPE } from './HelperConstants';
import TableCard from "./TableCard";
import React,{ useEffect, useState } from "react";

const GeneratedEmissionsView = ({ summaryData, dbDisplayName, beginDate, endDate, selectedVehicleClasses, vehicleClasses, tableData, graphData, defaultToMonthView, isTd, methodology, selectedGroups, macroScale, metricUnitPrefs, userSettings }) => {

    const [viewDataByMonth, setViewDataByMonth] = useState(defaultToMonthView);
    const [formattedGraphData, setFormattedGraphData] = useState([]);
    const [showDiscreteData, setShowDiscreteData] = useState(false);
    const [splitByDrivetrain, setSplitByDrivetrain] = useState(false);
    const [drivetrainsWithData, setDrivetrainsWithData] = useState({});
    
    useEffect(() => {
        setFormattedGraphData([]); // wipe graph data array

        setDrivetrainsWithData(splitByDrivetrain ?
            {
                "gasolineGenerated": summaryData.gasolineGhgGenerated !== 0,
                "dieselGenerated": summaryData.dieselGhgGenerated !== 0,
                "bevGenerated": summaryData.bevGhgGenerated !== 0,
                "phevGenerated": summaryData.phevGhgGenerated !== 0,
                "cngGenerated": summaryData.cngGhgGenerated !== 0,
            } : {
                "generated": summaryData.ghgGenerated !== 0,
            }
        );

        const updatedGraphData = compileGeneratedEmissionsViewGraphData(graphData, viewDataByMonth, splitByDrivetrain, showDiscreteData);
        setFormattedGraphData(updatedGraphData);
    }, [graphData, summaryData, viewDataByMonth, showDiscreteData, splitByDrivetrain]);

    const emissionsCardData = ((methodology?.value !== 'moves3') ?
        [
            {
                value: formatData('ghg', summaryData.ghgGenerated),
                label: getEmissionsUnits('ghg', metricUnitPrefs.emissions, macroScale, true),
                labelHover: getEmissionsUnits('ghg', metricUnitPrefs.emissions, macroScale, false),
                caption: "GHG Emissions",
                subcaption: "(Including GHG from kWh gen.)",
                id: "generated-ghg-card"
            },
            {
                value: formatData('co2', summaryData.co2Generated),
                label: getEmissionsUnits('co2', metricUnitPrefs.emissions, macroScale, true),
                labelHover: getEmissionsUnits('co2', metricUnitPrefs.emissions, macroScale, false),
                caption: <div>Carbon Dioxide (CO<sub>2</sub>)</div>,
                subcaption: "(Tailpipe)",
                id: "generated-co2-card"
            },
            {
                value: formatData('ch4', summaryData.ch4Generated),
                label: getEmissionsUnits('ch4', metricUnitPrefs.emissions, macroScale, true),
                labelHover: getEmissionsUnits('ch4', metricUnitPrefs.emissions, macroScale, false),
                caption: <div>Methane (CH<sub>4</sub>)</div>,
                subcaption: "(Tailpipe)",
                id: "generated-methane-card"
            },
            {
                value: formatData('nox', summaryData.noxGenerated),
                label: getEmissionsUnits('nox', metricUnitPrefs.emissions, macroScale, true),
                labelHover: getEmissionsUnits('nox', metricUnitPrefs.emissions, macroScale, false),
                caption: <div>Nitrous Oxide (NO<sub>x</sub>)</div>,
                subcaption: "(Tailpipe)",
                id: "generated-nox-card"
            },
            {
                value: formatData('bioco2', summaryData.bioco2Generated),
                label: getEmissionsUnits('bioco2', metricUnitPrefs.emissions, macroScale, true),
                labelHover: getEmissionsUnits('bioco2', metricUnitPrefs.emissions, macroScale, false),
                caption: <div>Biofuel CO<sub>2</sub> (BioCO<sub>2</sub>)</div>,
                subcaption: "(Tailpipe)",
                id: "generated-biofuel-card"
            }
        ]
        :
        [
            {
                value: formatData('ghg', summaryData.ghgGenerated),
                label: getEmissionsUnits('ghg', metricUnitPrefs.emissions, macroScale, true),
                labelHover: getEmissionsUnits('ghg', metricUnitPrefs.emissions, macroScale, false),
                caption: "GHG Emissions",
                subcaption: "(Including GHG from kWh gen.)",
                id: "generated-ghg-card"
            },
            {
                value: formatData('co2', summaryData.co2Generated),
                label: getEmissionsUnits('co2', metricUnitPrefs.emissions, macroScale, true),
                labelHover: getEmissionsUnits('co2', metricUnitPrefs.emissions, macroScale, false),
                caption: <div>Carbon Dioxide (CO<sub>2</sub>)</div>,
                subcaption: "(Tailpipe)",
                id: "generated-co2-card"
            },
            {
                value: formatData('co', summaryData.coGenerated),
                label: getEmissionsUnits('co', metricUnitPrefs.emissions, macroScale, true),
                labelHover: getEmissionsUnits('co', metricUnitPrefs.emissions, macroScale, false),
                caption: <div>Carbon Monoxide (CO)</div>,
                subcaption: "(Tailpipe)",
                id: "generated-co-card"
            },
            {
                value: formatData('nox', summaryData.noxGenerated),
                label: getEmissionsUnits('nox', metricUnitPrefs.emissions, macroScale, true),
                labelHover: getEmissionsUnits('nox', metricUnitPrefs.emissions, macroScale, false),
                caption: <div>Nitrous Oxide (NO<sub>x</sub>)</div>,
                subcaption: "(Tailpipe)",
                id: "generated-nox-card"
            },
            {
                value: formatData('pm10', summaryData.pm10Generated),
                label: getEmissionsUnits('pm10', metricUnitPrefs.emissions, macroScale, true),
                labelHover: getEmissionsUnits('pm10', metricUnitPrefs.emissions, macroScale, false),
                caption: "Particulate Matter 10\u00B5m (PM 10\u00B5m)",
                subcaption: "(Tailpipe)",
                id: "generated-pm10-card"
            },
            {
                value: formatData('pm25', summaryData.pm25Generated),
                label: getEmissionsUnits('pm25', metricUnitPrefs.emissions, macroScale, true),
                labelHover: getEmissionsUnits('pm25', metricUnitPrefs.emissions, macroScale, false),
                caption: "Particulate Matter 2.5\u00B5m (PM 2.5\u00B5m)",
                subcaption: "(Tailpipe)",
                id: "generated-pm25-card"
            },
        ]
    )

    const fleetCompositionCardData = compileFleetCompositionCardData(summaryData);
    const generatedEmissionsByDrivetrainData = compileGeneratedEmissionsByDrivetrainData(summaryData);
    const distanceBreakdownByDrivetrainData = compileDistanceBreakdownByDrivetrainData(summaryData);

    const graphUnits = getEmissionsUnits('ghg', metricUnitPrefs.emissions, macroScale, false);

    return (
        <>
            <CardGrid
                cardInfoArray={emissionsCardData}
                cardMinWidth="215px"
                cardMaxWidth="250px"
                cardHeight="130px"
                gridLayout="repeat(auto-fit, minmax(215px,1fr))"
            />
            <MidGrid
                columnLayout="2fr minmax(350px, 1fr)"
            >
                <GraphCard
                    height="auto"
                    minWidth="500px"
                    maxWidth="1200px"
                    title={`${viewDataByMonth ? "Monthly" : "Weekly"} Emissions`}
                    subtitle={`${splitByDrivetrain ? "By Fuel Type " : ""}${!showDiscreteData ? "(Cumulative)" : "(Discrete)"}`}
                    data={formattedGraphData}
                    barInfo={splitByDrivetrain ? emissionsByDrivetrainBars : combinedEmissionsBars}
                    yAxisLabel={`GHG (${graphUnits})`}
                    units={graphUnits}
                    xAxisKey="month"
                    showHoverLabels={false}
                    viewDataByMonth={viewDataByMonth}
                    xAxisLabel={viewDataByMonth ? null : 'Week Starting'}
                    showDiscreteData={showDiscreteData}
                    splitByDrivetrain={splitByDrivetrain}
                    setViewDataByMonth={setViewDataByMonth}
                    setShowDiscreteData={setShowDiscreteData}
                    setSplitByDrivetrain={setSplitByDrivetrain}
                    drivetrainsWithData={drivetrainsWithData}
                    metricUnitPrefs={metricUnitPrefs}
                    macroScale={macroScale}
                    userSettings={userSettings}
                />
                <div>
                    <DistributionCard
                        title="Fleet Composition"
                        subtitle="All Vehicles"
                        height="105px"
                        minWidth="350px"
                        maxWidth="700px"
                        dataTypes={fleetDrivetrains}
                        data={fleetCompositionCardData}
                        dataType="allVehicles"
                        colorHoverValues
                        useLongLabels
                        hideLegend
                    />
                    <DistributionCard
                        title="Distance Breakdown"
                        subtitle="By Fuel Type"
                        height="105px"
                        minWidth="350px"
                        maxWidth="700px"
                        dataTypes={fleetDrivetrains}
                        data={distanceBreakdownByDrivetrainData}
                        dataType="allDistance"
                        metricUnitPrefs={metricUnitPrefs}
                        colorHoverValues
                        useLongLabels
                        hideLegend
                        isTd={isTd}
                    />
                    <DistributionCard
                        title="Cumulative Emissions"
                        subtitle="By Fuel Type"
                        height="105px"
                        minWidth="350px"
                        maxWidth="700px"
                        dataTypes={fleetDrivetrains}
                        data={generatedEmissionsByDrivetrainData}
                        dataType="generatedEmissions"
                        metricUnitPrefs={metricUnitPrefs}
                        macroScale={macroScale}
                        colorHoverValues
                        useLongLabels
                        hideLegend
                    />
                    <DistributionCard
                        height="55px"
                        minWidth="350px"
                        maxWidth="700px"
                        dataTypes={fleetDrivetrains}
                        data={fleetCompositionCardData}
                        onlyLegend
                    />
                </div>
            </MidGrid>
            <TableCard
                height="600px"
                width="auto"
                title="Emissions Data"
                subtitle="for all fleet vehicles"
                tableType={GENERATED_TABLE_TYPE}
                beginDate={beginDate}
                endDate={endDate}
                tableData={tableData}
                selectedGroups={selectedGroups}
                selectedVehicleClasses={selectedVehicleClasses}
                vehicleClasses={vehicleClasses}
                dbDisplayName={dbDisplayName}
                methodology={methodology}
                isTd={isTd}
                metricUnitPrefs={metricUnitPrefs}
                macroScale={macroScale}
                userSettings={userSettings}
            />
        </>
    )
};

export default GeneratedEmissionsView;